import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../../ui";
import type { WizardData } from "../hooks/useWizardState";
import { ROOM_LOCATIONS } from "../constants";

interface Props {
  data: WizardData;
  onDone: () => void;
}

export function SuccessScreen({ data, onDone }: Props) {
  const autoName = [data.brand, data.subcategoryLabel].filter(Boolean).join(" ");
  const displayName = data.name || autoName;
  const room = ROOM_LOCATIONS.find((r) => r.id === data.location);

  return (
    <div className="animate-fade-in-up flex flex-col items-center">
      {/* Checkmark */}
      <div className="mt-8 mb-5 animate-scale-in">
        <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center ring-4 ring-green-500/20">
          <CheckCircle2 className="h-14 w-14 text-green-500" />
        </div>
      </div>

      <h2 className="text-2xl font-light tracking-tight text-center">
        Your asset is ready!
      </h2>
      <p className="text-sm text-muted-foreground mt-1 text-center px-4">
        {displayName} has been added successfully
      </p>

      {/* Summary Card */}
      <div className="mx-6 mt-7 w-full max-w-sm">
        <div className="p-5 rounded-2xl border-2 border-green-500/30 bg-green-500/[0.05]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold truncate pr-2">{displayName}</h3>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600 bg-green-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
              Recently Added
            </span>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Brand
              </p>
              <p className="text-xs font-medium mt-0.5 truncate">
                {data.brand || "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Model
              </p>
              <p className="text-xs font-medium mt-0.5 truncate">
                {data.model || "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Location
              </p>
              <p className="text-xs font-medium mt-0.5 truncate">
                {room?.label || "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Year
              </p>
              <p className="text-xs font-medium mt-0.5">
                {data.purchaseYear || "N/A"}
              </p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-green-500/10 space-y-1">
            <p className="text-[11px] text-muted-foreground">
              Health baseline created
            </p>
            <p className="text-[11px] text-muted-foreground">
              Maintenance plan attached
            </p>
            {data.documents.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {data.documents.length} document{data.documents.length > 1 ? "s" : ""} uploaded
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Done Button */}
      <div className="px-6 mt-6 w-full max-w-sm">
        <Button onClick={onDone} className="w-full rounded-xl">
          Done
        </Button>
        <p className="text-xs text-muted-foreground text-center mt-3 mb-6">
          Your asset has been saved and is now visible in your assets list
        </p>
      </div>
    </div>
  );
}
